import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http'
import { Facility } from '../types/Facility';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators'
import { environment } from '../../../environments/environment'
import { FacilityDetail } from '../types/FacilityDetail';
import { ProspectListItem } from '../types/ProspectListItem';
import { EstimatesAndActuals } from '../types/EstimatesAndActuals';
import { Status } from '../types/Status';
import { BusinessModel } from '../types/BusinessModel';
import { BusinessCategory } from '../types/BusinessCategory'
import { BidCompany } from '../types/BidCompany';
import { NotificationService } from "../../common/component/notification/notification.service";
import { IndustrialGasUOM } from '../types/IndustrialGasUOM';
import { Currency } from '../types/Currency';
import { CurrencyConversion } from '../types/CurrencyConversion';
import { TextDataResponse } from '../../common/model/TextDataResponse';
import { Site } from "src/app/plant/types/Site";
import { IndustrialGases } from "../types/IndustrialGases";

@Injectable()
export class FacilityService {

  constructor(private http: HttpClient, private notificationService: NotificationService) {

  }

  getSiteAndProvince(siteId: number): Observable<Facility> {
    return this.http.get<Facility>(environment.apiBaseUrl + 'site/' + siteId + '/province')
      .pipe(catchError(this.handleError<Facility>('getSiteAndProvince')))
  }

  getAllProductionActivity(): Observable<FacilityDetail[]> {
    return this.http.get<FacilityDetail[]>(environment.apiBaseUrl + 'facility/productionactivity')
      .pipe(catchError(this.handleError<FacilityDetail[]>('getAllProductionActivity')))
  }

  getProductionActivityById(productionActivityId: number): Observable<FacilityDetail[]> {
    return this.http.get<FacilityDetail[]>(environment.apiBaseUrl + 'facility/productionactivity/' + productionActivityId)
      .pipe(catchError(this.handleError<FacilityDetail[]>('getProductionActivityById')))
  }

  GetAllProspectDriver(): Observable<ProspectListItem[]> {
    return this.http.get<ProspectListItem[]>(environment.apiBaseUrl + 'facility/prospectdriver')
      .pipe(catchError(this.handleError<ProspectListItem[]>('GetAllProspectDriver')))
  }

  getFacilityEstimatesAndActuals(capacity: number, activityProcessFeedstockId: number, productionFacilityId: number): Observable<EstimatesAndActuals[]> {
    return this.http.get<EstimatesAndActuals[]>(environment.apiBaseUrl + `facility/estimatesandactuals?capacity=${capacity}&activityProcessFeedstockId=${activityProcessFeedstockId}&productionFacilityId=${productionFacilityId}`)
      .pipe(catchError(this.handleError<EstimatesAndActuals[]>('getFacilityEstimatesAndActuals')))
  }

  getCurrencyExchangeRateAsync(currency: string): Observable<Currency> {
    return this.http.get<Currency>(environment.apiBaseUrl + 'facility/currencyexchangerate/' + currency)
      .pipe(catchError(this.handleError<Currency>('GetCurrencyExchangeRateAsync')))
  }

  LoadAttachmentContent(productionFacilityId: number, fileName: string): Observable<string> {
    return this.http.get(environment.apiBaseUrl + `facility/fileContent/${productionFacilityId}/${fileName}`, { responseType: 'text' })
      .pipe(catchError(this.handleError<string>('LoadAttachmentContent')))
  }

  GetCurrencyExchangeYears(): Observable<number[]> {
    return this.http.get<number[]>(environment.apiBaseUrl + 'facility/currencyexchangeyears')
      .pipe(catchError(this.handleError<number[]>('GetCurrencyExchangeYears')))
  }

  getAllStatus(): Observable<Status[]> {
    return this.http.get<Status[]>(environment.apiBaseUrl + 'facility/statuses')
      .pipe(catchError(this.handleError<Status[]>('getAllStatus')))
  }

  GetSupplyTypes(): Observable<ProspectListItem[]> {
    return this.http.get<ProspectListItem[]>(environment.apiBaseUrl + 'facility/supplytypes')
      .pipe(catchError(this.handleError<ProspectListItem[]>('GetSupplyTypes')))
  }

  GetSupplyModes(): Observable<ProspectListItem[]> {
    return this.http.get<ProspectListItem[]>(environment.apiBaseUrl + 'facility/supplymodes')
      .pipe(catchError(this.handleError<ProspectListItem[]>('GetSupplyModes')))
  }

  GetBusinessModels(): Observable<BusinessModel[]> {
    return this.http.get<BusinessModel[]>(environment.apiBaseUrl + 'facility/businessmodels')
      .pipe(catchError(this.handleError<BusinessModel[]>('GetBusinessModels')))
  }

  GetIndustrialGasUOMs(): Observable<IndustrialGasUOM[]> {
    return this.http.get<IndustrialGasUOM[]>(environment.apiBaseUrl + 'facility/industrialgasuom')
      .pipe(catchError(this.handleError<IndustrialGasUOM[]>('GetIndustrialGasUOMs')))
  }

  getBidCompnaies(companyName: string): Observable<BidCompany[]> {
    return this.http.get<BidCompany[]>(environment.apiBaseUrl + `company/getcompanies?companyName=${companyName}`)
      .pipe(catchError(this.handleError<BidCompany[]>('GetBidCompnaies')))
  }

  getBusinessCategories(): Observable<BusinessCategory[]> {
    return this.http.get<BusinessCategory[]>(environment.apiBaseUrl + 'facility/businesscategories')
      .pipe(catchError(this.handleError<BusinessCategory[]>('getBusinessCategories')))
  }

  LoadProductionFacility(productionFacilityId: number): Observable<Facility> {
    return this.http.get<Facility>(environment.apiBaseUrl + 'facility/' + productionFacilityId)
      .pipe(catchError(this.handleError<Facility>('LoadProductionFacility')))
  }

  saveFacility(facility: Facility): Observable<any> {
    return this.http.post<Facility>(environment.apiBaseUrl + 'facility/save', facility)
      .pipe(catchError(this.handleError<Facility>('SaveFacility')))
  }

  deleteFacility(productionFacilityId: number): Observable<any> {
    return this.http.delete<any>(environment.apiBaseUrl + 'facility/delete/' + productionFacilityId)
      .pipe(catchError(this.handleError<any>('deleteFacility')))
  }

  ConvertCurrency(productionFacilityId: number, exchangeRateYear: number, sourceCurrency: string, destinationCurrency: string): Observable<CurrencyConversion> {
    return this.http.get<CurrencyConversion>(environment.apiBaseUrl + `facility/currencyconversion/${productionFacilityId}/${exchangeRateYear}/${sourceCurrency}/${destinationCurrency}`)
      .pipe(catchError(this.handleError<CurrencyConversion>('ConvertCurrency')))
  }

  ValidateGasUOM(estimatesAndActuals: EstimatesAndActuals[]): Observable<TextDataResponse> {
    return this.http.post<TextDataResponse>(environment.apiBaseUrl + 'facility/industrialGasUomValidate', estimatesAndActuals)
      .pipe(catchError(this.handleError<TextDataResponse>('ValidateGasUOM')))
  }

  getCoordinates(siteId: number): Observable<Site>{
    return this.http.get<Site>(environment.apiBaseUrl + 'site/' + siteId + '/details');
  }

  getIndustrailGases(): Observable<IndustrialGases[]> {
    return this.http.get<IndustrialGases[]>(environment.apiBaseUrl + 'facility/industrialgases')
      .pipe(catchError(this.handleError<IndustrialGases[]>('industrialgases')))
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.notificationService.alert("error", "Error on the method: " + operation, error);
      return of(result as T);
    }

  }

}
